// Everyone who holds a role, as an audience — route /customers/send-lists.
//
// THIS PAGE IS THE POINT OF THE ROLES FEATURE. Recording that somebody manages a store
// is bookkeeping until it can answer the question it was recorded for: who has to be
// told. Sunset notices, release notes, training invitations — every one of those is
// "reach this group on purpose", and before this the only way to build the list was to
// eyeball `customers.position`, which is free text and quietly gets it wrong in both
// directions (the assistant manager is swept in, the one spelled "GM" is missed).
//
// THE SCOPE AND ITS ID TRAVEL TOGETHER. `GET /customer-roles/:role` refuses one
// without the other rather than widening to everybody, because widening is the single
// mistake that matters when the answer is a mailing list. This page never sends a
// half-filled narrowing: choosing a scope kind alone changes nothing until something
// is picked, and the unnarrowed query is a deliberate choice ("All", which says so).
//
// AND IT SHOWS WHO IT CANNOT REACH. "12 brand managers" and "12, of whom 9 have an
// email address" are different facts. The second is the one that matters here, so the
// people with no address are counted out loud rather than silently dropped from the
// copyable list — a phone-only contact is ordinary on this desk (migration 018), not
// a data-entry error, and somebody has to decide to call them.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { api } from '../../api/client.js';
import OrganizationPicker from './OrganizationPicker.jsx';
import {
  ROLE_ORDER, SCOPE_LABELS, roleLabel, emailList, unreachableCount,
} from './customer-roles.js';

const SCOPE_ORDER = ['brand', 'organization', 'location'];

export default function SendLists() {
  const [role, setRole] = useState('store_manager');
  const [scope, setScope] = useState('');
  const [scopeId, setScopeId] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [organizationName, setOrganizationName] = useState('');

  const [brands, setBrands] = useState([]);
  const [locations, setLocations] = useState([]);
  const [result, setResult] = useState({ status: 'loading', rows: [], error: null });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api.get('/brands', { signal: controller.signal })
      .then((body) => setBrands(body?.data ?? []))
      .catch(() => setBrands([]));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!organizationId) { setLocations([]); return undefined; }
    const controller = new AbortController();
    api.get(`/organizations/${organizationId}/locations`, { signal: controller.signal })
      .then((body) => setLocations(body?.data ?? []))
      .catch(() => setLocations([]));
    return () => controller.abort();
  }, [organizationId]);

  // The narrowing is applied only when BOTH halves are present. A scope kind with
  // nothing picked yet is an unfinished thought, not a request for everybody.
  const narrowed = Boolean(scope && scopeId);

  const changeScope = useCallback((next) => {
    setScope(next);
    // The id names a row in the old scope's table and would name an unrelated row in
    // the new one. Organization is the exception: the picker's value IS the id.
    setScopeId(next === 'organization' ? organizationId : '');
  }, [organizationId]);

  useEffect(() => {
    const controller = new AbortController();
    const query = narrowed
      ? `?scope=${encodeURIComponent(scope)}&scope_id=${encodeURIComponent(scopeId)}`
      : '';
    setResult((previous) => ({ ...previous, status: 'loading', error: null }));
    setCopied(false);
    api.get(`/customer-roles/${encodeURIComponent(role)}${query}`, { signal: controller.signal })
      .then((body) => setResult({ status: 'ready', rows: body?.data ?? [], error: null }))
      .catch((failure) => {
        if (failure?.name === 'AbortError') return;
        setResult({ status: 'error', rows: [], error: failure });
      });
    return () => controller.abort();
  }, [role, scope, scopeId, narrowed]);

  const addresses = useMemo(() => emailList(result.rows), [result.rows]);
  const unreachable = useMemo(() => unreachableCount(result.rows), [result.rows]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(addresses.join('; '));
      setCopied(true);
    } catch {
      // Clipboard access can be refused outright (an insecure origin, a policy). The
      // textarea below is always on screen for exactly this reason, so there is
      // nothing to recover from and nothing to apologise for.
      setCopied(false);
    }
  }

  const scopeControl = () => {
    if (scope === 'brand') {
      return (
        <select className="field-input" value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}>
          <option value="">Choose a brand…</option>
          {brands.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.name}{brand.is_active ? '' : ' (retired)'}
            </option>
          ))}
        </select>
      );
    }

    if (!scope) return null;

    const picker = (
      <OrganizationPicker
        organizationId={scope === 'organization' ? scopeId : organizationId}
        organizationName={organizationName}
        onChange={(next) => {
          setOrganizationId(next.organizationId);
          setOrganizationName(next.organizationName);
          setScopeId(scope === 'organization' ? next.organizationId : '');
        }}
      />
    );

    if (scope === 'organization') return picker;

    return (
      <div className="space-y-2">
        {picker}
        <select className="field-input" value={scopeId} disabled={!organizationId}
                onChange={(e) => setScopeId(e.target.value)}>
          <option value="">
            {organizationId ? 'Choose a store…' : 'Choose an organization first'}
          </option>
          {locations.map((location) => (
            <option key={location.id} value={location.id}>{location.name}</option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <Link to="/customers" className="text-xs text-brand-700 hover:underline dark:text-brand-300">
          ← Customers
        </Link>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">Send lists</h1>
        <p className="text-sm text-ink-secondary">
          Everyone holding a role, for a sunset notice, a release note or a training
          invitation. Roles are recorded on each contact&rsquo;s page.
        </p>
      </div>

      <section className="rounded-lg border border-line bg-surface p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="text-xs font-medium text-ink-secondary">Role</span>
            <select className="field-input mt-1" value={role}
                    onChange={(e) => setRole(e.target.value)}>
              {ROLE_ORDER.map((value) => (
                <option key={value} value={value}>{roleLabel(value)}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-ink-secondary">Narrow to</span>
            <select className="field-input mt-1" value={scope}
                    onChange={(e) => changeScope(e.target.value)}>
              <option value="">All</option>
              {SCOPE_ORDER.map((value) => (
                <option key={value} value={value}>{SCOPE_LABELS[value]}</option>
              ))}
            </select>
          </label>

          {scope && (
            <div className="block">
              <span className="text-xs font-medium text-ink-secondary">
                {SCOPE_LABELS[scope]}
              </span>
              <div className="mt-1">{scopeControl()}</div>
            </div>
          )}
        </div>

        {scope && !scopeId && (
          <p className="mt-3 text-sm text-ink-muted">
            Showing everyone. Pick {scope === 'organization' ? 'an' : 'a'}{' '}
            {SCOPE_LABELS[scope].toLowerCase()} to narrow the list.
          </p>
        )}
      </section>

      {result.status === 'error' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm text-red-700 dark:text-red-300">
            Could not build this list. {result.error?.message}
          </p>
        </div>
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-ink">
            {result.status === 'loading' ? 'Loading…' : (
              <>
                {result.rows.length.toLocaleString()}{' '}
                {roleLabel(role).toLowerCase()}
                {result.rows.length === 1 ? '' : 's'}
              </>
            )}
          </h2>
          {result.status === 'ready' && result.rows.length > 0 && (
            <p className="text-sm text-ink-muted">
              {addresses.length.toLocaleString()} email{addresses.length === 1 ? '' : 's'}
              {unreachable > 0 && (
                <span className="text-amber-700 dark:text-amber-400">
                  {' '}&middot; {unreachable} with no address, who will need a call
                </span>
              )}
            </p>
          )}
        </div>

        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full table-fixed divide-y divide-line text-sm">
            <colgroup>
              <col className="w-[28%]" />
              <col className="w-[32%]" />
              <col className="w-[20%]" />
              <col className="w-[20%]" />
            </colgroup>
            <thead className="bg-sunken text-left text-xs font-medium uppercase tracking-wide text-ink-secondary">
              <tr>
                <th scope="col" className="px-4 py-2.5">Name</th>
                <th scope="col" className="px-4 py-2.5">Email</th>
                <th scope="col" className="px-4 py-2.5">Phone</th>
                <th scope="col" className="px-4 py-2.5">Position</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {result.status === 'ready' && result.rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-sm text-ink-muted">
                    Nobody holds this role{narrowed ? ' here' : ' yet'}. Roles are recorded
                    on a contact&rsquo;s page, under Roles.
                  </td>
                </tr>
              )}
              {result.rows.map((person) => (
                <tr key={person.id} className="group transition-colors hover:bg-sunken">
                  <td className="px-4 py-2">
                    <Link to={`/customers/${person.id}`}
                          className="block truncate font-medium text-ink hover:text-brand-700 dark:hover:text-brand-300">
                      {person.name}
                    </Link>
                  </td>
                  <td className="truncate px-4 py-2 text-ink-secondary" title={person.email ?? undefined}>
                    {person.email ?? (
                      <span className="text-ink-muted">no email address</span>
                    )}
                  </td>
                  <td className="truncate px-4 py-2 text-ink-secondary">{person.phone}</td>
                  <td className="truncate px-4 py-2 text-ink-secondary">{person.position}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* The textarea is not a fallback for the copy button — it is the primary
            affordance, and the button is the convenience. Clipboard access is refused
            on an insecure origin and by some policies, and a send list that cannot be
            got out of the page is the one failure this screen must not have. */}
        {addresses.length > 0 && (
          <div className="rounded-lg border border-line bg-surface p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-ink">Addresses</h3>
              <button type="button" className="btn-ghost ring-1 ring-line-strong" onClick={copy}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <textarea
              readOnly
              rows={3}
              className="field-input mt-2 font-mono text-xs"
              value={addresses.join('; ')}
              onFocus={(e) => e.target.select()}
            />
            <p className="mt-2 text-xs text-ink-muted">
              Semicolon separated, for the To or Bcc line. Deduplicated: somebody who
              manages two stores appears once.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
